import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileText, Link2 } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  RESOLVABLE_RISK_EVENT_STATUSES,
  RiskEventStatus,
  apiError,
  riskSeverityTone,
  riskStatusTone,
  type RiskEventDetail,
} from './lib';
import { useStaffNames } from './useStaffNames';

const UNASSIGNED = '__unassigned__';

/**
 * One case: the rule's own sentence, the audited rows it was built from, the
 * note thread, and the four moves a reviewer can make.
 *
 * `risk.review` opened this dialog (the queue's own route gate); every WRITE
 * below is gated again on `risk.resolve`, matching `RiskEventsController` —
 * seeing a case and acting on it are one tier apart, never the reverse.
 */
export default function CaseDetailDialog({
  eventId,
  onClose,
  onChanged,
}: {
  eventId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canResolve = can('risk.resolve');
  const { nameOf, options: staffOptions } = useStaffNames();
  const [assigning, setAssigning] = useState(false);

  const [detail, setDetail] = useState<RiskEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { format: formatMoney } = useCurrency(detail?.branchId ?? null);

  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<RiskEventStatus>(RiskEventStatus.UNDER_REVIEW);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RiskEventDetail>(`/risk/events/${eventId}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => setError(apiError(e, t('risk.case.loadFailed'))))
      .finally(() => setLoading(false));
  }, [eventId, t]);

  useEffect(load, [load]);

  const assignTo = async (reviewerId: string | null) => {
    setAssigning(true);
    try {
      await api.patch(`/risk/events/${eventId}/assign`, { reviewerId });
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('risk.case.assignFailed')));
    } finally {
      setAssigning(false);
    }
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/risk/events/${eventId}/notes`, { body: noteDraft.trim() });
      setNoteDraft('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('risk.case.noteFailed')));
    } finally {
      setSavingNote(false);
    }
  };

  const resolve = async () => {
    const terminal = resolveStatus !== RiskEventStatus.UNDER_REVIEW;
    if (terminal && !resolveNote.trim()) return;
    setResolving(true);
    try {
      await api.post(`/risk/events/${eventId}/resolve`, {
        status: resolveStatus,
        note: resolveNote.trim() || undefined,
      });
      toast.success(t('risk.case.resolved'));
      setResolveNote('');
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('risk.case.resolveFailed')));
    } finally {
      setResolving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('risk.case.title')}
      className="max-w-2xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {loading && !detail ? (
        <LoadingState label={t('common.loading')} />
      ) : error && !detail ? (
        <ErrorState message={error} onRetry={load} />
      ) : !detail ? (
        <EmptyState title={t('risk.case.notFound')} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={riskStatusTone(detail.status)}>{t(`risk.status.${detail.status}`)}</Badge>
            <Badge tone={riskSeverityTone(detail.severity)}>{t(`risk.severity.${detail.severity}`)}</Badge>
            <Badge tone="neutral">{t(`risk.rule.${detail.rule}`)}</Badge>
          </div>

          {/* The whole finding as one sentence, rendered at detection time.
              Never a score, never regenerated against today's threshold. */}
          <div className="rounded-lg bg-subtle p-3 text-sm">{detail.explanation}</div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <Fact label={t('risk.case.subject')} value={nameOf(detail.subjectUserId) ?? t('risk.case.noSubject')} />
            <Fact
              label={t('risk.case.window')}
              value={`${formatDateTime(detail.windowStart, i18n.language)} – ${formatDateTime(detail.windowEnd, i18n.language)}`}
            />
            <Fact label={t('risk.case.observedCount')} value={String(detail.observedCount)} tabular />
            <Fact label={t('risk.case.observedAmount')} value={formatMoney(detail.observedAmount)} tabular />
            {detail.thresholdCount !== null && (
              <Fact label={t('risk.case.thresholdCount')} value={String(detail.thresholdCount)} tabular />
            )}
            {detail.thresholdAmount !== null && (
              <Fact label={t('risk.case.thresholdAmount')} value={formatMoney(detail.thresholdAmount)} tabular />
            )}
            {!canResolve && (
              <Fact
                label={t('risk.case.assignedReviewer')}
                value={nameOf(detail.assignedReviewerId) ?? t('risk.case.unassigned')}
              />
            )}
          </dl>

          {canResolve && (
            <Field label={t('risk.case.assignedReviewer')}>
              <Select
                value={detail.assignedReviewerId ?? UNASSIGNED}
                disabled={assigning}
                onChange={(e) =>
                  void assignTo(e.target.value === UNASSIGNED ? null : e.target.value)
                }
              >
                <option value={UNASSIGNED}>{t('risk.case.unassigned')}</option>
                {staffOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Icon as={Link2} size="sm" />
              {t('risk.case.evidence')}
            </h4>
            {detail.evidence.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('risk.case.noEvidence')}</p>
            ) : (
              <ul className="divide-y rounded-lg border text-sm">
                {detail.evidence.map((row) => (
                  <li key={row.id} className="space-y-0.5 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span>{row.description}</span>
                      {row.amount !== null && <span className="tabular-nums">{formatMoney(row.amount)}</span>}
                    </div>
                    <p className="text-xs text-ink-muted">
                      {formatDateTime(row.occurredAt, i18n.language)}
                      {' · '}
                      {row.entityType}
                      {row.actorId && ` · ${nameOf(row.actorId)}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Icon as={FileText} size="sm" />
              {t('risk.case.notes')}
            </h4>
            {detail.notes.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('risk.case.noNotes')}</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {detail.notes.map((note) => (
                  <li key={note.id} className="rounded-lg border p-2.5">
                    <p className="whitespace-pre-wrap">{note.body}</p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {nameOf(note.authorId) ?? t('risk.case.system')} ·{' '}
                      {formatDateTime(note.createdAt, i18n.language)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {canResolve && (
              <div className="mt-2 flex gap-2">
                <Textarea
                  rows={2}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder={t('risk.case.addNotePlaceholder')}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  disabled={!noteDraft.trim() || savingNote}
                  onClick={() => void addNote()}
                >
                  {t('common.add')}
                </Button>
              </div>
            )}
          </section>

          {canResolve && detail.status !== RiskEventStatus.CONFIRMED && detail.status !== RiskEventStatus.FALSE_POSITIVE && detail.status !== RiskEventStatus.DISMISSED && (
            <section className="space-y-2 rounded-lg border p-3">
              <h4 className="text-sm font-semibold">{t('risk.case.resolveTitle')}</h4>
              <Field label={t('risk.case.outcome')}>
                <Select
                  value={resolveStatus}
                  onChange={(e) => setResolveStatus(e.target.value as RiskEventStatus)}
                >
                  {RESOLVABLE_RISK_EVENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`risk.status.${s}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('risk.case.resolutionNote')}
                required={resolveStatus !== RiskEventStatus.UNDER_REVIEW}
                hint={
                  resolveStatus === RiskEventStatus.FALSE_POSITIVE
                    ? t('risk.case.falsePositiveHint')
                    : resolveStatus !== RiskEventStatus.UNDER_REVIEW
                      ? t('risk.case.terminalNoteHint')
                      : undefined
                }
              >
                <Textarea rows={3} value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} />
              </Field>
              <Button
                onClick={() => void resolve()}
                disabled={
                  resolving ||
                  (resolveStatus !== RiskEventStatus.UNDER_REVIEW && !resolveNote.trim())
                }
              >
                {t('risk.case.submitResolution')}
              </Button>
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
}

function Fact({ label, value, tabular }: { label: string; value: string; tabular?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</dt>
      <dd className={tabular ? 'tabular-nums' : undefined}>{value}</dd>
    </div>
  );
}
